import { useTranslation } from 'react-i18next';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { StepIndicator } from './StepIndicator';
import { VARIATION_TYPES } from './constants';
import type { VariationType, AssociationGroup } from '@/lib/types';

interface EditGroupStepProps {
  editGroupName: string;
  editGroupDescription: string;
  groupToEdit: AssociationGroup | null;
  isUpdating: boolean;
  variationType: VariationType;
  onSetEditGroupName: (name: string) => void;
  onSetEditGroupDescription: (description: string) => void;
  onBack: () => void;
  onSave: () => void;
}

export function EditGroupStep({
  editGroupName,
  editGroupDescription,
  groupToEdit,
  isUpdating,
  variationType,
  onSetEditGroupName,
  onSetEditGroupDescription,
  onBack,
  onSave,
}: EditGroupStepProps) {
  const { t } = useTranslation();

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('associations.editGroup')}</DialogTitle>
        <DialogDescription>{t('associations.editGroupDescription')}</DialogDescription>
      </DialogHeader>

      <StepIndicator step="edit_group" variationType={variationType} />

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="editGroupName">{t('associations.groupName')} *</Label>
          <Input
            id="editGroupName"
            value={editGroupName}
            onChange={(e) => onSetEditGroupName(e.target.value)}
            placeholder={t('associations.groupNamePlaceholder')}
            maxLength={50}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="editGroupDescription">{t('associations.description')}</Label>
          <Input
            id="editGroupDescription"
            value={editGroupDescription}
            onChange={(e) => onSetEditGroupDescription(e.target.value)}
            placeholder={t('associations.descriptionPlaceholder')}
            maxLength={200}
          />
        </div>

        {groupToEdit && (
          <div className="p-3 bg-muted rounded-lg text-sm text-muted-foreground">
            <span className="font-medium">{t('associations.variationType')}:</span>{' '}
            {t(VARIATION_TYPES.find((v) => v.value === groupToEdit.variationType)?.labelKey || 'associations.variationTypes.related')}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onBack}>
          <ChevronLeft className="size-4 mr-1" />
          {t('common.back')}
        </Button>
        <Button onClick={onSave} disabled={isUpdating || !editGroupName.trim()}>
          {isUpdating && <Loader2 className="size-4 mr-2 animate-spin" />}
          {t('common.save')}
        </Button>
      </DialogFooter>
    </>
  );
}
